import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const Footer = () => {
  return (
    <footer
      id="contact"
      data-testid="contact-footer"
      className="relative overflow-hidden bg-zinc-950 text-white"
    >
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[60rem] -translate-x-1/2 rounded-full bg-sky-500/15 blur-[120px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-[1400px] px-6 py-32 md:px-12 md:py-44">
        <Reveal>
          <p className="mb-6 text-xs font-bold uppercase tracking-[0.35em] text-sky-400">
            Ready when you are
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <a
            href="tel:4066963771"
            data-testid="footer-lets-talk-link"
            className="group block"
          >
            <h2 className="font-display text-[clamp(3rem,10vw,9rem)] font-black leading-[0.9] tracking-tighter transition-colors duration-500 group-hover:text-sky-400">
              LET'S TALK
              <ArrowUpRight
                className="ml-4 inline h-[0.6em] w-[0.6em] text-sky-400 transition-transform duration-500 group-hover:rotate-45"
                aria-hidden="true"
              />
            </h2>
          </a>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
            One call is all it takes. Free quotes, friendly crew, and glass so
            clear you'll forget it's there.
          </p>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {[
            {
              icon: Phone,
              label: "Call or Text",
              value: "406-696-3771",
              href: "tel:4066963771",
              testId: "footer-phone-link",
            },
            {
              icon: Mail,
              label: "Email",
              value: "skyhighwindowwashing1@gmail.com",
              href: "mailto:skyhighwindowwashing1@gmail.com",
              testId: "footer-email-link",
            },
            {
              icon: MapPin,
              label: "Service Area",
              value: "Billings, Montana",
              href: "https://maps.google.com/?q=Billings,+MT",
              testId: "footer-location-link",
            },
          ].map((item, i) => (
            <motion.a
              key={item.label}
              href={item.href}
              data-testid={item.testId}
              target={item.label === "Service Area" ? "_blank" : undefined}
              rel={item.label === "Service Area" ? "noopener noreferrer" : undefined}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group flex items-center gap-5 rounded-md border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm transition-all duration-500 hover:border-sky-400/50 hover:bg-white/[0.06]"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-400 transition-transform duration-300 group-hover:scale-110">
                <item.icon className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                  {item.label}
                </span>
                <span className="block truncate text-sm font-bold text-white md:text-base">
                  {item.value}
                </span>
              </span>
            </motion.a>
          ))}
        </div>

        <div className="mt-24 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center">
          <p
            data-testid="footer-brand"
            className="font-display text-sm font-black tracking-tight text-white"
          >
            SKY-HIGH<span className="text-sky-400">.</span>{" "}
            <span className="font-medium text-zinc-500">Window Washing</span>
          </p>
          <div className="flex flex-col items-start gap-2 md:items-end">
            <a
              href="https://skyhighwindowwashing.com"
              data-testid="footer-domain-link"
              className="text-xs uppercase tracking-[0.25em] text-zinc-500 transition-colors duration-300 hover:text-sky-400"
            >
              skyhighwindowwashing.com
            </a>
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-600">
              © {new Date().getFullYear()} — Billings, Montana
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
